import { ReactNode, isValidElement } from "react";

/**
 * Recursively search through a React node tree and return the first node that matches the given predicate.
 * @param node - The root React node to start the search from.
 * @param predicate - A type guard function that determines if a node matches the desired criteria.
 * @returns The first matching node, or undefined if no match is found.
 *
 * @example
 * Instead of: source={children.props.children[0].props.children[2].props.href}
 * const tweetUrl = findInReactTree<string>(
 *  children,
 *  (value): value is string =>
 *  typeof value === "string" &&
 *  /^https?:\/\/(www\.)?(twitter\.com|x\.com)\/[^/]+\/status\/\d+/.test(value)
 * );
 */

function findInReactTree<T>(
  node: ReactNode,
  predicate: (value: unknown) => value is T
): T | undefined {
  if (!node) return;

  // direct match
  if (predicate(node)) return node;

  // array of children → search each
  if (Array.isArray(node)) {
    for (const child of node) {
      const found = findInReactTree(child, predicate);
      if (found) return found;
    }
  }

  // React element → search its props
  if (isValidElement(node)) {
    for (const value of Object.values(node.props)) {
      const found = findInReactTree(value, predicate);
      if (found) return found;
    }
  }

  return;
}

export default findInReactTree;
